import { type RefObject, useState } from 'react';
import { Route, useLocation } from 'react-router-dom';
import { useScrollToTopFixIOS } from '@hungryroot/environment';
import { useMealTypePageContent } from '@hungryroot/exp-175-diet-driven-preferences';
import { type ContainerFrequencyOptions } from '@hungryroot/rest';
import { tagGroups } from '@hungryroot/tags';
import { ControlOccasionPreferenceSelect } from '../../components/ControlOccasionPreferenceSelect/ControlOccasionPreferenceSelect';
import { ControlTagSelect } from '../../components/ControlTagSelect/ControlTagSelect';
import { FrequencyPicker } from '../../components/FrequencyPicker/FrequencyPicker';
import { QuizQuestionScreen } from '../../components/QuizQuestionScreen/QuizQuestionScreen';
import { ToggleFour } from '../../components/Toggle/ToggleFour/ToggleFour';
import { ROUTE_SNACKS_FREQUENCY } from '../../defs/routes';
import {
  foodPreferenceAnalytics,
  containerFrequencyAnalytics,
  type QuizPreferenceItem,
} from '../../segment/quizAnalytics';

interface Props {
  testid: string;
  scrollRef: RefObject<HTMLDivElement>;
}

export function Snacks(props: Props) {
  const { testid, scrollRef } = props;
  const [trackedItems, setTrackedItems] = useState<QuizPreferenceItem[]>([]);
  const [trackedFrequency, setTrackedFrequency] =
    useState<ContainerFrequencyOptions | null>(null);
  const { pathname } = useLocation();

  useScrollToTopFixIOS(scrollRef);

  const { toggles, subcopy } =
    useMealTypePageContent({
      defaultSubcopy:
        'We stock snacks that meet our standards—crafted from whole, trusted ingredients.',
      defaultToggleContent: (
        <ControlTagSelect
          ariaLabel="Select foods you like for snacks"
          tagGroupId={tagGroups.snack}
          question="snacks"
          layoutVariant="three-column"
          defaultValue={[]}
          onTrackSelected={(items: QuizPreferenceItem[]) => {
            setTrackedItems(items);
          }}
        >
          {({ active, tag, index }) => {
            const { id: tagId, name: tagName, image_url: imgSrc } = tag;
            return (
              <ToggleFour
                active={active}
                testid={`${testid}-toggle-tag-${tagId}`}
                tagName={tagName}
                tagId={tagId}
                imgSrc={imgSrc}
                style={{ animationDelay: `${0.05 * index + 0.5}s` }}
              />
            );
          }}
        </ControlTagSelect>
      ),
      variantSubcopy:
        "Choose as many as you'd like. Your picks help us learn your tastes and tailor our recommendations.",
      variantToggleContent: (variant: number) => (
        <ControlOccasionPreferenceSelect
          testid="snacks-preference-options"
          ariaLabel="Select foods that you like for snacks"
          question="snacks"
          variant={variant === 1 ? 'simple' : 'detail'}
          onTrackSelected={(items: QuizPreferenceItem[]) => {
            setTrackedItems(items);
          }}
        />
      ),
    }) || {};

  return (
    <QuizQuestionScreen
      testid={testid}
      heroText="What snacks look good?"
      subCopy={subcopy}
      onSubmitAnalytics={() => {
        if (pathname === ROUTE_SNACKS_FREQUENCY) {
          containerFrequencyAnalytics('snacks', trackedFrequency);
        } else {
          foodPreferenceAnalytics('snacks', trackedItems);
        }
      }}
    >
      {toggles}
      <Route path={ROUTE_SNACKS_FREQUENCY}>
        <FrequencyPicker
          testid={testid}
          controlTestId={`${testid}-question-frequency-options`}
          headingText="How many days a week would you like us to cover snacks?"
          question="snacksFrequency"
          setTrackedFrequency={setTrackedFrequency}
        />
      </Route>
    </QuizQuestionScreen>
  );
}
